/**
 * What the case page's controls do, kept apart from how they look.
 *
 * **Nothing here decides what is possible.** The card arrives from the server
 * with its offers already worked out — `next_action` — and every act below is
 * one of the server's commands, posted as it stands. The pure functions at the
 * foot are the few judgements a form has to make about itself (is every charge
 * answered, has a choice that needs a paper got one), pinned in `acting.test.ts`.
 */

import { useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import type {
  CaseSummary,
  ChoiceView,
  NextActionView,
  SourceFileReceipt,
} from '../../api/client'

/** What the intake door has always written on the complaint it files — the
 *  office's own word for the paper, which is what prints in a संदर्भ line. The
 *  server reads it as the complaint's kind until paper kinds are set on it. */
export const COMPLAINT_WORD = 'शिकायत'

/** Everything a recorded act can change, refreshed together — the card, the
 *  register row, the papers, the audit. The server recomputes; nothing here
 *  patches its own copy. */
export function useRefreshCase(caseId: string): () => void {
  const client = useQueryClient()
  return () => {
    void client.invalidateQueries({ queryKey: ['case', caseId] })
    void client.invalidateQueries({ queryKey: ['cases'] })
    void client.invalidateQueries({ queryKey: ['source-files', caseId] })
    void client.invalidateQueries({ queryKey: ['audit', caseId] })
    void client.invalidateQueries({ queryKey: ['notifications'] })
  }
}

export interface Uploaded {
  fileId: string
  receipt: SourceFileReceipt
  /** Set when the paper is on the file but its kind could not be recorded. */
  kindNotRecorded: string | null
}

/**
 * Put a paper on the file and say what kind it is.
 *
 * `recordsDocument` files it as that external document on the rail — the
 * enquiry report is D04, the inquiry report D12, the penalty order D15 — through
 * the upload's own recording, which is the one path that already does it.
 *
 * **The kind is recorded after the upload, and its failure is not the
 * upload's.** A paper that is on the file stays on it; the act the paper came
 * with still goes ahead, and the page says the kind was not recorded.
 */
export async function uploadPaper(
  caseId: string,
  file: File,
  kind: string | null | undefined,
  recordsDocument?: string | null,
): Promise<Uploaded> {
  const receipt = await api.uploadSourceFile(caseId, file, {
    document_type: recordsDocument ?? undefined,
    other_description: kind === 'complaint' && !recordsDocument ? COMPLAINT_WORD : undefined,
  })
  const fileId = receipt.file.id
  let kindNotRecorded: string | null = null
  if (kind) {
    try {
      await api.setPaperKind(caseId, fileId, { preflight: false, kind })
    } catch (error) {
      kindNotRecorded = error instanceof Error ? error.message : 'the kind was not recorded'
    }
  }
  return { fileId, receipt, kindNotRecorded }
}

/* ------------------------------------------------------------ pure parts -- */

/**
 * What the card's *Draft it* hands the drafting screen: start the draft.
 *
 * The link used to open a nearly empty page where the officer pressed
 * *Generate* a second time (walkthrough 32.34). The request to start travels in
 * the link's **state** — the history entry's, never the address — so it cannot
 * be bookmarked, pasted or shared into starting a draft, and the drafting
 * screen takes it off the entry the moment it arrives, so a reload or coming
 * back to the page finds nothing to act on.
 */
export interface DraftNow {
  draftNow: true
  /** The card's *We don't have it — draft anyway*: the gap is already known,
   *  so the drafting screen does not ask about it again. */
  anyway: boolean
  /** Which noting the card asked for, as the server named it. The noting is
   *  one document drafted for several purposes, and the card that says *send
   *  the draft chargesheet up* must not start the noting that starts
   *  proceedings (walkthrough 32.34). Null for every other document. */
  purpose: string | null
}

export function draftNow(anyway?: boolean | null, purpose?: string | null): DraftNow {
  return { draftNow: true, anyway: Boolean(anyway), purpose: purpose ?? null }
}

/** Only exactly what `draftNow` makes. Anything else in a history entry's
 *  state — another screen's, or nothing — starts no draft. */
export function isDraftNow(state: unknown): state is DraftNow {
  return (
    typeof state === 'object' &&
    state !== null &&
    (state as { draftNow?: unknown }).draftNow === true
  )
}

/** Running means a clock has started and not closed — including one that has
 *  run out, which is the one the officer most needs to see. */
const RUNNING = new Set(['running', 'due-soon', 'overdue', 'paused'])

/** The periods the case's head lists: every one running, in the server's order. */
export function runningClocks<T extends { state: string }>(
  clocks: readonly T[] | null | undefined,
): T[] {
  return (clocks ?? []).filter((clock) => RUNNING.has(clock.state))
}

/**
 * Whether the case's head is already showing this period.
 *
 * The head lists every running deadline, each with its authority, its citation
 * and its due date. The card under it showed its own period again — the same
 * pill, the same label — so a case with one deadline read as a case with two
 * (walkthrough 32.34). The card keeps its period only where the head says
 * nothing about it: one that cannot start yet, or one already satisfied.
 */
export function headShowsClock(
  clocks: readonly { key: string; state: string }[] | null | undefined,
  key: string | null | undefined,
): boolean {
  if (!key) return false
  return runningClocks(clocks).some((clock) => clock.key === key)
}

/**
 * Whether the register row shows the case's most urgent period beside its card:
 * only once that period is due soon or overdue, and only when it is not the
 * period the card itself shows. A card held on an act the record lacks carries
 * no clock of its own, and an overdue period behind it must not vanish from the
 * register (compliance review, 26 Sep 2026).
 */
export function urgentElsewhere(
  row: Pick<CaseSummary, 'worst_clock_state' | 'worst_clock_label' | 'next_action'>,
): boolean {
  const state = row.worst_clock_state
  if (state !== 'overdue' && state !== 'due-soon') return false
  return row.next_action?.clock?.label !== row.worst_clock_label
}

/** The card's situation, in the words A3 gives them. */
export function situationWords(situation: NextActionView['situation']): string {
  switch (situation) {
    case 'do':
      return 'Something to do'
    case 'wait':
      return 'Waiting'
    case 'needs':
      return 'Needs something first'
    case 'none':
      return 'Nothing due'
  }
}

export interface DecisionForm {
  choice: ChoiceView | null
  /** Charges ticked or named, when the choice charges anybody. */
  charges: number
  penalty: string | null
  /** A paper attached to the decision. */
  paper: boolean
}

/** Why the decision cannot be recorded yet, or `null` when it can. Said in a
 *  sentence under the button, never by a silent grey. */
export function decisionBlocked(form: DecisionForm): string | null {
  if (!form.choice) return 'Pick the answer the authority gave.'
  if (form.choice.needs_charges && form.charges === 0)
    return 'Tick the allegations the authority decided to charge.'
  if (form.choice.needs_penalty && !form.penalty)
    return 'Pick the penalty the authority proposed — Rule 11’s list.'
  if (form.choice.paper && !form.paper)
    return 'This answer comes with a signed paper — attach it.'
  return null
}

/** Why his answers cannot be recorded yet, or `null` when they can. */
export function answersBlocked(
  charges: readonly number[],
  answers: Readonly<Record<number, string>>,
): string | null {
  const open = charges.filter((n) => !answers[n])
  if (open.length === 0) return null
  return open.length === 1
    ? `Charge ${open[0]} needs his answer.`
    : `Charges ${open.join(', ')} need his answer.`
}

/**
 * The reasons recorded in writing for an extension he asked for.
 *
 * The rule asks for reasons in writing, and the authority's are on its own
 * signed order. What the ledger records here is what the office knows: that
 * he asked, and in which paper — never a reason made up to fill the field.
 */
export function extensionReason(filename: string | null | undefined): string {
  return filename
    ? `Granted on the Charged Officer's request — his letter on the file: ${filename}.`
    : "Granted on the Charged Officer's request."
}
